import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/envago/Button/Button';
import { NoMatchInfo } from '../aiReadingMachine';
import { instructionKeyFor, instructionParamsFor } from './GuidancePanel';

const TITLE_KEY_BY_REASON: Record<string, string> = {
    wrongMedium: 'aiReading.notFound.titleWrongMedium',
    numberMismatch: 'aiReading.notFound.titleNumberMismatch',
};

interface MeterNotFoundPanelProps {
    info: NoMatchInfo | null;
    onRetry: () => void;
    // Hand over to the classic job list ("Manuell erfassen") — primary when nothing was
    // recognizable at all, since retrying the camera is unlikely to help there.
    onManualCapture: () => void;
    // Leave the assistant entirely (back to the home screen).
    onExit: () => void;
}

// "This is probably the wrong meter" moment: the panel explains WHY the meter could not be
// assigned (foreign medium / number matches no job / nothing readable). With a KNOWN reason
// (wrong medium / wrong number) the user is standing at the wrong meter, so retry leads;
// with nothing recognized at all, manual capture in the job list leads instead. Assigning the
// reading to a different job by hand was removed deliberately (invited mis-assignments).
const MeterNotFoundPanel = ({ info, onRetry, onManualCapture, onExit }: MeterNotFoundPanelProps) => {
    const { t } = useTranslation();
    const nothingRecognized = !info || info.reason === 'notRecognized';
    // Same key + params as the spoken announcement — the user reads exactly what they hear
    // (except the meter number, which is shown here but deliberately never read aloud).
    const description = t(instructionKeyFor('identify', 'noMatch', info), instructionParamsFor(info, t));

    return (
        <div className={'absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4 max-h-[60%] overflow-y-auto'}>
            <h3>{t(TITLE_KEY_BY_REASON[info?.reason ?? ''] ?? 'aiReading.notFound.title')}</h3>
            <p data-testid="not-found-description" className={'text-sm text-gray-600'}>
                {description}
            </p>
            <div className={'flex flex-col gap-2 mt-3'}>
                {nothingRecognized ? (
                    <>
                        <Button primary={true} block={true} onClick={onManualCapture}>
                            {t('aiReading.buttons.manualCapture')}
                        </Button>
                        <Button secondary={true} block={true} onClick={onRetry}>
                            {t('aiReading.buttons.retry')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button primary={true} block={true} onClick={onRetry}>
                            {t('aiReading.buttons.retry')}
                        </Button>
                        <Button secondary={true} block={true} onClick={onManualCapture}>
                            {t('aiReading.buttons.manualCapture')}
                        </Button>
                    </>
                )}
                <Button secondary={true} ghost={true} block={true} onClick={onExit}>
                    {t('aiReading.buttons.exit')}
                </Button>
            </div>
        </div>
    );
};

export default MeterNotFoundPanel;
